import { api } from './apiClient';

/**
 * Journal Control Center service — read APIs for the analytics/exceptions/360
 * surfaces (mounted at /journals) plus the enriched journal list + lifecycle
 * actions (on /finance/journal-entries). The apiClient auto-unwraps the
 * `{ status, data }` envelope, so each call returns `data` directly.
 */

export interface JournalSummary {
  totalEntries: number;
  byStatus: Record<string, { count: number; amount: number }>;
  postedThisPeriod: { amount: number; vsPrevPeriodPct: number };
  pendingApproval: { count: number; amount: number };
  drafts: { count: number; amount: number };
  reversed: { count: number; amount: number };
  requiresAttention: number;
  unbalanced: { count: number };
  netDebitCredit: { debit: number; credit: number; balanced: boolean };
  byType: { journal_type: string | null; count: number; amount: number }[];
  bySource: { source_type: string | null; count: number; amount: number }[];
}

export interface JournalTrendPoint {
  date: string; count: number; debit: number; credit: number; net: number;
  posted: number; draft: number; reversed: number; pending: number;
}
export interface JournalTrend { window: string; points: JournalTrendPoint[] }

export interface JournalException {
  id: string; kind: string; severity: 'critical' | 'warning' | string;
  title: string; detail: string; count: number; amount: number;
  route: string; action: string;
}
export interface JournalExceptions {
  summary: { critical: number; warning: number; total: number };
  exceptions: JournalException[];
}

export interface JournalListFilters {
  fromDate?: string; toDate?: string; status?: string; financialYear?: string;
  journalType?: string; sourceType?: string; accountId?: number;
  search?: string; minAmount?: number; maxAmount?: number; createdBy?: number;
  page?: number; limit?: number;
}

const qs = (f?: Record<string, any>) => {
  if (!f) return '';
  const p = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') p.append(k, String(v)); });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const journalWorkspaceService = {
  summary: (): Promise<JournalSummary> => api.get('/journals/summary'),
  trend: (window: '7D' | '30D' | '90D' | 'FY' = '30D'): Promise<JournalTrend> =>
    api.get(`/journals/trend?window=${window}`),
  exceptions: (): Promise<JournalExceptions> => api.get('/journals/exceptions'),
  detail: (id: number): Promise<any> => api.get(`/journals/${id}/detail`),

  // Enriched list (Agent A) — controller still serves `journalEntries` + `count`
  // and additionally exposes `pagination`.
  list: (filters?: JournalListFilters): Promise<{ journalEntries: any[]; count: number; pagination?: any }> =>
    api.get(`/finance/journal-entries${qs(filters)}`),

  // Lifecycle actions (Agent A)
  submit: (id: number): Promise<any> => api.post(`/finance/journal-entries/${id}/submit`, {}),
  approve: (id: number): Promise<any> => api.post(`/finance/journal-entries/${id}/approve`, {}),
  reject: (id: number, reason: string): Promise<any> => api.post(`/finance/journal-entries/${id}/reject`, { reason }),
  post: (id: number): Promise<any> => api.post(`/finance/journal-entries/${id}/post`, {}),
  reverse: (id: number, reason?: string): Promise<any> => api.post(`/finance/journal-entries/${id}/reverse`, { reason }),
};

export default journalWorkspaceService;
